// Paletas de tema de la plataforma: fuente única.
//
// Vivían repartidas: las CLASES en `contexts/BrandingContext.jsx` y los RÓTULOS ("Grafito", "Azul
// Petróleo") en `pages/SettingsPage.jsx`, bajo el mismo nombre `THEMES` y con dos formas distintas.
// La landing pública también las muestra (sección "Marca propia"), y no puede importar ninguno de
// esos dos archivos: BrandingContext arrastra `services/api` y Settings es una página entera. Por
// eso viven acá, sin dependencias, y los dos archivos originales las importan.

// ⚠️ Los tokens de interacción (`hoverBorder`, `focusRing`, `accentColor`, …) están escritos
// COMPLETOS, con su prefijo `hover:` / `focus:` adentro de la cadena. No es verbosidad: Tailwind
// emite una clase sólo si encuentra su LITERAL escaneando el código, y no evalúa expresiones. Armar
// el estado en el llamador —`hover:${theme.childBorder}`— produce una cadena de clase válida cuya
// regla CSS nunca se generó: el hover queda transparente, el build pasa en verde y no hay warning.
// Escritos acá, el scanner los ve. Verificable con `scripts/verificar_clases_tema.py`.
//
// Las escalas las genera `scratchpad/agregar_tokens_interaccion.py` a partir de la familia de
// color, que se DERIVA de `activeItemBg`. Un tema nuevo hereda los nueve tokens declarando ese
// campo; no hay una segunda lista que se pueda olvidar de actualizar.
export const THEMES = {
  indigo: {
    activeItemBg:     "bg-indigo-50",
    activeItemText:   "text-indigo-700",
    activeItemBorder: "border-indigo-100",
    childBorder:      "border-indigo-200",
    childActiveBg:    "bg-indigo-100",
    childActiveText:  "text-indigo-700",
    swatchBg:         "bg-indigo-600",
    solidBg:          "bg-indigo-700",
    solidHex:         "#4338ca",
    heroFromHex:      "#4f46e5",
    heroToHex:        "#4338ca",
    cardHeaderBg:     "bg-indigo-700",
    heroBorderColor:  "border-indigo-500",
    hoverBorder:      "hover:border-indigo-300",
    hoverBg:          "hover:bg-indigo-50/40",
    hoverText:        "hover:text-indigo-700",
    hoverTextFuerte:  "hover:text-indigo-800",
    groupHoverText:   "group-hover:text-indigo-700",
    focusBorder:      "focus:border-indigo-400",
    focusRing:        "focus:ring-indigo-300",
    focusBg:          "focus:bg-indigo-50/70",
    accentColor:      "accent-indigo-600",
    ringAccent:       "ring-indigo-400",
    hoverBgFuerte:    "hover:bg-indigo-100",
    textFuerte:       "text-indigo-800",
    textSuave:        "text-indigo-500",
    textTenue:        "text-indigo-100",
    borderMedio:      "border-indigo-300",
    ringSuave:        "ring-indigo-100",
    focusRingSuave:   "focus:ring-indigo-100",
    focusWithinRing:  "focus-within:ring-indigo-300",
    accentBorderL:    "border-l-indigo-500",
    accentBorderLSuave: "border-l-indigo-400",
  },
  emerald: {
    activeItemBg:     "bg-emerald-50",
    activeItemText:   "text-emerald-700",
    activeItemBorder: "border-emerald-100",
    childBorder:      "border-emerald-200",
    childActiveBg:    "bg-emerald-100",
    childActiveText:  "text-emerald-700",
    swatchBg:         "bg-emerald-600",
    solidBg:          "bg-emerald-700",
    solidHex:         "#047857",
    heroFromHex:      "#059669",
    heroToHex:        "#047857",
    cardHeaderBg:     "bg-emerald-700",
    heroBorderColor:  "border-emerald-500",
    hoverBorder:      "hover:border-emerald-300",
    hoverBg:          "hover:bg-emerald-50/40",
    hoverText:        "hover:text-emerald-700",
    hoverTextFuerte:  "hover:text-emerald-800",
    groupHoverText:   "group-hover:text-emerald-700",
    focusBorder:      "focus:border-emerald-400",
    focusRing:        "focus:ring-emerald-300",
    focusBg:          "focus:bg-emerald-50/70",
    accentColor:      "accent-emerald-600",
    ringAccent:       "ring-emerald-400",
    hoverBgFuerte:    "hover:bg-emerald-100",
    textFuerte:       "text-emerald-800",
    textSuave:        "text-emerald-500",
    textTenue:        "text-emerald-100",
    borderMedio:      "border-emerald-300",
    ringSuave:        "ring-emerald-100",
    focusRingSuave:   "focus:ring-emerald-100",
    focusWithinRing:  "focus-within:ring-emerald-300",
    accentBorderL:    "border-l-emerald-500",
    accentBorderLSuave: "border-l-emerald-400",
  },
  violet: {
    activeItemBg:     "bg-violet-50",
    activeItemText:   "text-violet-700",
    activeItemBorder: "border-violet-100",
    childBorder:      "border-violet-200",
    childActiveBg:    "bg-violet-100",
    childActiveText:  "text-violet-700",
    swatchBg:         "bg-violet-600",
    solidBg:          "bg-violet-700",
    solidHex:         "#6d28d9",
    heroFromHex:      "#7c3aed",
    heroToHex:        "#6d28d9",
    cardHeaderBg:     "bg-violet-700",
    heroBorderColor:  "border-violet-500",
    hoverBorder:      "hover:border-violet-300",
    hoverBg:          "hover:bg-violet-50/40",
    hoverText:        "hover:text-violet-700",
    hoverTextFuerte:  "hover:text-violet-800",
    groupHoverText:   "group-hover:text-violet-700",
    focusBorder:      "focus:border-violet-400",
    focusRing:        "focus:ring-violet-300",
    focusBg:          "focus:bg-violet-50/70",
    accentColor:      "accent-violet-600",
    ringAccent:       "ring-violet-400",
    hoverBgFuerte:    "hover:bg-violet-100",
    textFuerte:       "text-violet-800",
    textSuave:        "text-violet-500",
    textTenue:        "text-violet-100",
    borderMedio:      "border-violet-300",
    ringSuave:        "ring-violet-100",
    focusRingSuave:   "focus:ring-violet-100",
    focusWithinRing:  "focus-within:ring-violet-300",
    accentBorderL:    "border-l-violet-500",
    accentBorderLSuave: "border-l-violet-400",
  },
  rose: {
    activeItemBg:     "bg-rose-50",
    activeItemText:   "text-rose-700",
    activeItemBorder: "border-rose-100",
    childBorder:      "border-rose-200",
    childActiveBg:    "bg-rose-100",
    childActiveText:  "text-rose-700",
    swatchBg:         "bg-rose-600",
    solidBg:          "bg-rose-700",
    solidHex:         "#be123c",
    heroFromHex:      "#e11d48",
    heroToHex:        "#be123c",
    cardHeaderBg:     "bg-rose-700",
    heroBorderColor:  "border-rose-500",
    hoverBorder:      "hover:border-rose-300",
    hoverBg:          "hover:bg-rose-50/40",
    hoverText:        "hover:text-rose-700",
    hoverTextFuerte:  "hover:text-rose-800",
    groupHoverText:   "group-hover:text-rose-700",
    focusBorder:      "focus:border-rose-400",
    focusRing:        "focus:ring-rose-300",
    focusBg:          "focus:bg-rose-50/70",
    accentColor:      "accent-rose-600",
    ringAccent:       "ring-rose-400",
    hoverBgFuerte:    "hover:bg-rose-100",
    textFuerte:       "text-rose-800",
    textSuave:        "text-rose-500",
    textTenue:        "text-rose-100",
    borderMedio:      "border-rose-300",
    ringSuave:        "ring-rose-100",
    focusRingSuave:   "focus:ring-rose-100",
    focusWithinRing:  "focus-within:ring-rose-300",
    accentBorderL:    "border-l-rose-500",
    accentBorderLSuave: "border-l-rose-400",
  },
  amber: {
    activeItemBg:     "bg-amber-50",
    activeItemText:   "text-amber-700",
    activeItemBorder: "border-amber-100",
    childBorder:      "border-amber-200",
    childActiveBg:    "bg-amber-100",
    childActiveText:  "text-amber-700",
    swatchBg:         "bg-amber-500",
    solidBg:          "bg-amber-600",
    solidHex:         "#d97706",
    heroFromHex:      "#d97706",
    heroToHex:        "#b45309",
    cardHeaderBg:     "bg-amber-600",
    heroBorderColor:  "border-amber-500",
    hoverBorder:      "hover:border-amber-400",
    hoverBg:          "hover:bg-amber-50/40",
    hoverText:        "hover:text-amber-700",
    hoverTextFuerte:  "hover:text-amber-800",
    groupHoverText:   "group-hover:text-amber-700",
    focusBorder:      "focus:border-amber-500",
    focusRing:        "focus:ring-amber-400",
    focusBg:          "focus:bg-amber-50/70",
    accentColor:      "accent-amber-700",
    ringAccent:       "ring-amber-500",
    hoverBgFuerte:    "hover:bg-amber-100",
    textFuerte:       "text-amber-900",
    textSuave:        "text-amber-600",
    textTenue:        "text-amber-100",
    borderMedio:      "border-amber-400",
    ringSuave:        "ring-amber-100",
    focusRingSuave:   "focus:ring-amber-100",
    focusWithinRing:  "focus-within:ring-amber-300",
    accentBorderL:    "border-l-amber-600",
    accentBorderLSuave: "border-l-amber-500",
  },
  slate: {
    activeItemBg:     "bg-slate-100",
    activeItemText:   "text-slate-700",
    activeItemBorder: "border-slate-200",
    childBorder:      "border-slate-300",
    childActiveBg:    "bg-slate-100",
    childActiveText:  "text-slate-700",
    swatchBg:         "bg-slate-600",
    solidBg:          "bg-slate-700",
    solidHex:         "#334155",
    heroFromHex:      "#475569",
    heroToHex:        "#334155",
    cardHeaderBg:     "bg-slate-700",
    heroBorderColor:  "border-slate-500",
    hoverBorder:      "hover:border-slate-300",
    hoverBg:          "hover:bg-slate-50/40",
    hoverText:        "hover:text-slate-700",
    hoverTextFuerte:  "hover:text-slate-800",
    groupHoverText:   "group-hover:text-slate-700",
    focusBorder:      "focus:border-slate-400",
    focusRing:        "focus:ring-slate-300",
    focusBg:          "focus:bg-slate-50/70",
    accentColor:      "accent-slate-600",
    ringAccent:       "ring-slate-400",
    hoverBgFuerte:    "hover:bg-slate-200",
    textFuerte:       "text-slate-800",
    textSuave:        "text-slate-500",
    textTenue:        "text-slate-100",
    borderMedio:      "border-slate-300",
    ringSuave:        "ring-slate-100",
    focusRingSuave:   "focus:ring-slate-100",
    focusWithinRing:  "focus-within:ring-slate-300",
    accentBorderL:    "border-l-slate-500",
    accentBorderLSuave: "border-l-slate-400",
  },
  cyan: {
    activeItemBg:     "bg-cyan-50",
    activeItemText:   "text-cyan-700",
    activeItemBorder: "border-cyan-100",
    childBorder:      "border-cyan-200",
    childActiveBg:    "bg-cyan-100",
    childActiveText:  "text-cyan-700",
    swatchBg:         "bg-cyan-500",
    solidBg:          "bg-cyan-700",
    solidHex:         "#0e7490",
    heroFromHex:      "#0891b2",
    heroToHex:        "#0e7490",
    cardHeaderBg:     "bg-cyan-700",
    heroBorderColor:  "border-cyan-500",
    hoverBorder:      "hover:border-cyan-300",
    hoverBg:          "hover:bg-cyan-50/40",
    hoverText:        "hover:text-cyan-700",
    hoverTextFuerte:  "hover:text-cyan-800",
    groupHoverText:   "group-hover:text-cyan-700",
    focusBorder:      "focus:border-cyan-400",
    focusRing:        "focus:ring-cyan-300",
    focusBg:          "focus:bg-cyan-50/70",
    accentColor:      "accent-cyan-600",
    ringAccent:       "ring-cyan-400",
    hoverBgFuerte:    "hover:bg-cyan-100",
    textFuerte:       "text-cyan-800",
    textSuave:        "text-cyan-500",
    textTenue:        "text-cyan-100",
    borderMedio:      "border-cyan-300",
    ringSuave:        "ring-cyan-100",
    focusRingSuave:   "focus:ring-cyan-100",
    focusWithinRing:  "focus-within:ring-cyan-300",
    accentBorderL:    "border-l-cyan-500",
    accentBorderLSuave: "border-l-cyan-400",
  },
  grafito: {
    activeItemBg:     "bg-stone-100",
    activeItemText:   "text-stone-700",
    activeItemBorder: "border-stone-200",
    childBorder:      "border-stone-300",
    childActiveBg:    "bg-stone-100",
    childActiveText:  "text-stone-700",
    swatchBg:         "bg-[#1e2533]",
    solidBg:          "bg-[#1e2533]",
    solidHex:         "#1e2533",
    heroFromHex:      "#292524",
    heroToHex:        "#1c1917",
    cardHeaderBg:     "bg-[#1e2533]",
    heroBorderColor:  "border-stone-600",
    hoverBorder:      "hover:border-stone-300",
    hoverBg:          "hover:bg-stone-50/40",
    hoverText:        "hover:text-stone-700",
    hoverTextFuerte:  "hover:text-stone-800",
    groupHoverText:   "group-hover:text-stone-700",
    focusBorder:      "focus:border-stone-400",
    focusRing:        "focus:ring-stone-300",
    focusBg:          "focus:bg-stone-50/70",
    accentColor:      "accent-stone-600",
    ringAccent:       "ring-stone-400",
    hoverBgFuerte:    "hover:bg-stone-200",
    textFuerte:       "text-stone-800",
    textSuave:        "text-stone-500",
    textTenue:        "text-stone-100",
    borderMedio:      "border-stone-300",
    ringSuave:        "ring-stone-100",
    focusRingSuave:   "focus:ring-stone-100",
    focusWithinRing:  "focus-within:ring-stone-300",
    accentBorderL:    "border-l-stone-500",
    accentBorderLSuave: "border-l-stone-400",
  },
  petroleo: {
    activeItemBg:     "bg-blue-50",
    activeItemText:   "text-blue-900",
    activeItemBorder: "border-blue-100",
    childBorder:      "border-blue-200",
    childActiveBg:    "bg-blue-100",
    childActiveText:  "text-blue-900",
    swatchBg:         "bg-[#0f4c75]",
    solidBg:          "bg-[#0f4c75]",
    solidHex:         "#0f4c75",
    heroFromHex:      "#0f4c75",
    heroToHex:        "#0a3352",
    cardHeaderBg:     "bg-[#0f4c75]",
    heroBorderColor:  "border-[#0f4c75]",
    hoverBorder:      "hover:border-blue-300",
    hoverBg:          "hover:bg-blue-50/40",
    hoverText:        "hover:text-blue-700",
    hoverTextFuerte:  "hover:text-blue-800",
    groupHoverText:   "group-hover:text-blue-700",
    focusBorder:      "focus:border-blue-400",
    focusRing:        "focus:ring-blue-300",
    focusBg:          "focus:bg-blue-50/70",
    accentColor:      "accent-blue-600",
    ringAccent:       "ring-blue-400",
    hoverBgFuerte:    "hover:bg-blue-100",
    textFuerte:       "text-blue-800",
    textSuave:        "text-blue-500",
    textTenue:        "text-blue-100",
    borderMedio:      "border-blue-300",
    ringSuave:        "ring-blue-100",
    focusRingSuave:   "focus:ring-blue-100",
    focusWithinRing:  "focus-within:ring-blue-300",
    accentBorderL:    "border-l-blue-500",
    accentBorderLSuave: "border-l-blue-400",
  },
};

// Rótulos, descripción y colores de muestra de cada tema (selector de Configuración y landing).
export const THEME_OPCIONES = [
  {
    key: "indigo",   label: "Índigo",        description: "Clásico inmobiliario",
    solidHex: "#4338ca", heroFrom: "#4f46e5", heroTo: "#4338ca",
    badge: { text: "Más usado", cls: "bg-emerald-100 text-emerald-700" },
  },
  {
    key: "emerald",  label: "Esmeralda",     description: "Natural y confiable",
    solidHex: "#047857", heroFrom: "#059669", heroTo: "#047857",
    badge: null,
  },
  {
    key: "violet",   label: "Violeta",       description: "Creativo y moderno",
    solidHex: "#6d28d9", heroFrom: "#7c3aed", heroTo: "#6d28d9",
    badge: null,
  },
  {
    key: "rose",     label: "Rosa",          description: "Cálido y cercano",
    solidHex: "#be123c", heroFrom: "#e11d48", heroTo: "#be123c",
    badge: null,
  },
  {
    key: "amber",    label: "Ámbar",         description: "Energético y llamativo",
    solidHex: "#d97706", heroFrom: "#d97706", heroTo: "#b45309",
    badge: null,
  },
  {
    key: "slate",    label: "Slate",         description: "Premium corporativo",
    solidHex: "#334155", heroFrom: "#475569", heroTo: "#334155",
    badge: { text: "Nuevo", cls: "bg-indigo-100 text-indigo-700" },
  },
  {
    key: "cyan",     label: "Cyan",          description: "Tecnológico y ágil",
    solidHex: "#0e7490", heroFrom: "#0891b2", heroTo: "#0e7490",
    badge: { text: "Nuevo", cls: "bg-indigo-100 text-indigo-700" },
  },
  {
    key: "grafito",  label: "Grafito",       description: "Minimalista oscuro",
    solidHex: "#1e2533", heroFrom: "#292524", heroTo: "#1c1917",
    badge: { text: "Premium", cls: "bg-amber-100 text-amber-700" },
  },
  {
    key: "petroleo", label: "Azul Petróleo", description: "Fintech elegante",
    solidHex: "#0f4c75", heroFrom: "#0f4c75", heroTo: "#0a3352",
    badge: { text: "Premium", cls: "bg-amber-100 text-amber-700" },
  },
];
